import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, RefreshCw, Film, Clapperboard, Send, Trash2, Settings, X } from "lucide-react";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import IgReelsConfigPanel from "./IgReelsConfigPanel";
import { STATUS_LABELS, STATUS_COLORS, fmtDateTime } from "./igReelsConstants";

// Reels de Instagram para Guía Secreta Dolomitas — cola de publicación programada. Clips de
// origen ya subidos a Backblaze B2 (ver scripts/sync-ig-reels-clips.py); el render con FFmpeg
// y la publicación corren en el backend (ig_reels.py), aquí solo se gestiona la cola.
export default function IgReelsBoard() {
  const [locations, setLocations] = useState(null);
  const [queue, setQueue] = useState(null);
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(() => {
    api.get("/ig-reels/locations").then(({ data }) => setLocations(data.locations)).catch(() => {});
    api.get("/ig-reels/queue").then(({ data }) => setQueue(data.queue)).catch((e) => toast.error(apiErr(e)));
    api.get("/ig-reels/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const renderNow = async (id) => {
    setBusyId(id);
    try { await api.post(`/ig-reels/queue/${id}/render-now`); toast.success("Renderizado."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };
  const publishNow = async (id) => {
    setBusyId(id);
    try { await api.post(`/ig-reels/queue/${id}/publish-now`); toast.success("Publicado en Instagram."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };
  const removeReel = async (id) => {
    if (!window.confirm("¿Borrar esta tarjeta de la cola?")) return;
    try { await api.delete(`/ig-reels/queue/${id}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sweepNow = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/ig-reels/sweep");
      toast.success(data.processed.length ? `${data.processed.length} procesada(s).` : "Nada pendiente de publicar hoy.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSweeping(false); }
  };

  if (!queue || !locations) return <div className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-pink-600" size={28} /></div>;

  return (
    <div className="space-y-4" data-testid="igreels-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Reels programados para Guía Secreta Dolomitas — render con FFmpeg + publicación en Instagram.</p>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowConfig((v) => !v)} data-testid="igreels-toggle-config"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <Settings size={15} /> Configuración
          </button>
          <button onClick={sweepNow} disabled={sweeping}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            {sweeping ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Comprobar pendientes
          </button>
          <button onClick={load} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><RefreshCw size={15} /></button>
          <button onClick={() => setShowCreate(true)} data-testid="igreels-create-btn"
            className="flex items-center gap-1.5 rounded-lg bg-pink-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-pink-700">
            <Plus size={15} /> Reel
          </button>
        </div>
      </div>

      {showConfig && <IgReelsConfigPanel config={config} onSaved={load} />}

      {!config?.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Faltan las credenciales de Instagram — la cola se puede rellenar y renderizar, pero no publicará hasta que las configures.
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs text-slate-400">
            <tr>
              <th className="px-4 py-2.5 font-medium">Lugar</th>
              <th className="px-4 py-2.5 font-medium">Caption</th>
              <th className="px-4 py-2.5 font-medium">Programado</th>
              <th className="px-4 py-2.5 font-medium">Estado</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {queue.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-slate-400">Cola vacía.</td></tr>
            ) : queue.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`igreels-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{r.location}</td>
                <td className="px-4 py-3 max-w-xs truncate text-slate-600" title={r.caption}>{r.caption || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(r.scheduledAt) || "sin fecha"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-500"}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                  {r.lastError && <div className="mt-1 max-w-xs truncate text-[11px] text-rose-600" title={r.lastError}>{r.lastError}</div>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => renderNow(r.id)} disabled={busyId === r.id || r.status === "rendering"}
                      title="Renderizar ahora" data-testid={`igreels-render-${r.id}`}
                      className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-50">
                      {busyId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Clapperboard size={14} />}
                    </button>
                    <button onClick={() => publishNow(r.id)} disabled={busyId === r.id || r.status !== "ready"}
                      title="Publicar ahora" data-testid={`igreels-publish-${r.id}`}
                      className="rounded-lg border border-slate-200 p-1.5 text-pink-600 hover:bg-pink-50 disabled:opacity-40">
                      <Send size={14} />
                    </button>
                    <button onClick={() => removeReel(r.id)} title="Borrar"
                      className="rounded-lg border border-slate-200 p-1.5 text-rose-500 hover:bg-rose-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <IgReelsCreateModal locations={locations} onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

// Formulario de alta — único caller, vive aquí en vez de en su propio fichero (YAGNI, mismo
// criterio que GrowthCreateModal en frontend/src/components/growth/GrowthBoard.jsx).
function IgReelsCreateModal({ locations, onClose, onCreated }) {
  const [location, setLocation] = useState(locations[0]?.location || "");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!location) { toast.warning("Elige un lugar"); return; }
    setBusy(true);
    try {
      await api.post("/ig-reels/queue", {
        location, caption,
        hashtags: hashtags.split(/[\s,]+/).map((h) => h.trim()).filter(Boolean),
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      toast.success("Añadido a la cola.");
      onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md space-y-3 rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900"><Film size={18} /> Nuevo reel</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="igreels-create-location"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {locations.length === 0 && <option value="">Sin clips sincronizados todavía</option>}
          {locations.map((l) => <option key={l.location} value={l.location}>{l.location} ({l.clips} clips)</option>)}
        </select>
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} maxLength={2200}
          placeholder="Caption" data-testid="igreels-create-caption" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="Hashtags, separados por espacio"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
          data-testid="igreels-create-schedule" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button onClick={submit} disabled={busy} data-testid="igreels-create-submit"
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700 disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />} Añadir a la cola
        </button>
      </div>
    </div>
  );
}
